// src/hooks/useQuranSettings.js
import { useEffect, useState } from 'react';
import { fetchReciters, fetchTranslations } from '../api/quranService';

export const ARABIC_FONTS = [
  { key: 'uthmani', label: 'Uthmani (Madinah Mushaf)', family: "'KFGQPC Uthmanic Script', 'Amiri Quran', serif" },
  { key: 'amiri', label: 'Amiri Quran', family: "'Amiri Quran', serif" },
  { key: 'uthman', label: 'Uthman', family: "'QuranFont', 'Amiri Quran', serif" },
];

export const ARABIC_SIZES = ['2xl', '3xl', '4xl'];

// Used until the lists load, or if they can't be fetched
const FALLBACK_RECITERS = [{ _id: '1', reciter_name: 'Alafasy' }, { _id: '2', reciter_name: 'Abdul Baseet' }];

const STORAGE_KEY = 'quranReaderSettings';
const DEFAULTS = { reciterId: '1', translationId: '2', fontKey: 'uthmani', sizeIndex: 1, showTranslation: true };

const readSaved = () => {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') };
  } catch {
    return DEFAULTS;
  }
};

// The lists rarely change, so they are fetched once per page load
let listsPromise = null;
const loadLists = () => {
  if (!listsPromise) {
    listsPromise = Promise.all([
      fetchReciters().catch(() => FALLBACK_RECITERS),
      fetchTranslations().catch(() => []),
    ]);
  }
  return listsPromise;
};

/**
 * Reader preferences (reciter, translation, Arabic font and size), remembered in this browser,
 * plus the reciters and translations to choose from.
 */
const useQuranSettings = () => {
  const [settings, setSettings] = useState(readSaved);
  const [reciters, setReciters] = useState(FALLBACK_RECITERS);
  const [translations, setTranslations] = useState([]);

  useEffect(() => {
    let cancelled = false;
    loadLists().then(([r, t]) => {
      if (cancelled) return;
      if (r?.length) setReciters(r);
      setTranslations(t || []);
    });
    return () => { cancelled = true; };
  }, []);

  const update = (changes) => {
    setSettings((prev) => {
      const next = { ...prev, ...changes };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Settings just won't be remembered
      }
      return next;
    });
  };

  const font = ARABIC_FONTS.find((f) => f.key === settings.fontKey) || ARABIC_FONTS[0];
  const reciterName = reciters.find((r) => String(r._id) === String(settings.reciterId))?.reciter_name || 'Reciter';

  return {
    ...settings,
    fontFamily: font.family,
    arabicSize: ARABIC_SIZES[settings.sizeIndex] || ARABIC_SIZES[1],
    reciters,
    translations,
    reciterName,
    update,
  };
};

export default useQuranSettings;
